class GtcPagination extends HTMLElement {
  constructor() {
    super();

    const dataElement = this.querySelector("[data-data]");
    if (!dataElement) {
      return;
    }

    this.data = JSON.parse(dataElement.textContent);

    this.gridSelector = this.querySelector(
      "[data-grid-selector]"
    ).dataset.gridSelector;
    this.grid = document.querySelector(this.gridSelector);

    this.count = this.querySelector("[data-count]");
    this.progress = this.querySelector("[data-progress]");
    this.previousButton = this.querySelector("[data-previous]");
    this.nextButton = this.querySelector("[data-next]");

    this.parts = new Map();

    for (let i = 0; i < this.data.pages; i += 1) {
      const page = i + 1;
      const size =
        (i === this.data.pages - 1
          ? this.data.items % this.data.page_size
          : this.data.page_size) || this.data.page_size;
      const url = new URL(window.location.toString());
      url.searchParams.set("page", (i + 1).toString());
      url.searchParams.set("view", "items");

      this.parts.set(page, {
        page,
        size,
        url,
        isLoaded: page === this.data.current_page,
      });
    }

    this.previousPart = this.parts.get(this.data.current_page - 1);
    this.nextPart = this.parts.get(this.data.current_page + 1);
  }

  connectedCallback() {
    this.previousButton?.addEventListener(
      "click",
      this.loadPrevious.bind(this)
    );
    this.nextButton?.addEventListener("click", this.loadNext.bind(this));

    if (this.hasAttribute("data-autoload-previous")) {
      while (this.previousPart) {
        this.loadPrevious();
      }
    }


    if (this.getAttribute("data-mode") === "infinite-scroll") {
      this.infiniteScrollIntersectionObserver();
    }
  }

  updateProgress = () => {
    let currentOffset = 0;
    let currentProgress = 0;
    let hasFoundLoadedPart = false;

    this.parts.forEach((part) => {
      if (part.isLoaded) {
        currentProgress += part.size;
        hasFoundLoadedPart = true;
      }

      if (!hasFoundLoadedPart) {
        currentOffset += part.size;
      }
    });

    this.count.textContent = currentProgress.toString();
    this.progress.style.setProperty(
      "--progress",
      `${(currentProgress / this.data.items) * 100}%`
    );
    this.progress.style.setProperty(
      "--offset",
      `${(currentOffset / this.data.items) * 100}%`
    );
  };

  updatePageParam = (partOrPage) => {
    const page =
      typeof partOrPage === "object" ? partOrPage.page : partOrPage;
    const params = new URLSearchParams(window.location.search);
    params.set("page", page.toString());

    window.history.replaceState(null, "", `?${params.toString()}`);
  };

  loadPart = async (part) => {
    if (part.isLoaded) {
      throw new Error("Pagination part is already loaded or loading");
    }

    part.isLoaded = true;
    this.updateProgress();

    const response = await fetch(part.url);
    return response.text();
  };

  insertItems = (position, text, autoFocus, pageNumber) => {
    this.grid.role = "alert";

    const content = document.createElement("div");
    content.insertAdjacentHTML(
      position === "previous" ? "afterbegin" : "beforeend",
      text
    );
    const children = Array.from(content.children);

    if (pageNumber != null) {
      children.forEach((el) => {
        el.dataset.page = String(pageNumber);
      });
    }

    if (position === "previous") {
      this.grid.prepend(...children);

      if (autoFocus) {
        children[children.length - 1]
          ?.querySelector("a, button, input, select, textarea")
          ?.focus();
      }
    } else {
      this.grid.append(...children);

      if (autoFocus) {
        children[0]
          ?.querySelector("a, button, input, select, textarea")
          ?.focus();
      }
    }

    if (this.intersectionObserver) {
      this.updateScrollTargets();
    }
  };

  loadPrevious = async (event) => {
    if (!this.previousPart || this._loadingPrevious) {
      return;
    }

    this._loadingPrevious = true;
    try {
      this.updatePageParam(this.previousPart);

      this.insertItems(
        "previous",
        await this.loadPart(this.previousPart),
        event !== undefined,
        this.previousPart.page
      );

      this.previousPart = this.parts.get(this.previousPart.page - 1);

      if (!this.previousPart && this.previousButton) {
        this.previousButton.remove();
      }
    } finally {
      this._loadingPrevious = false;
    }
  };

  loadNext = async (event) => {
    if (!this.nextPart || this._loadingNext) {
      return;
    }

    this._loadingNext = true;
    try {
      this.updatePageParam(this.nextPart);

      this.insertItems(
        "next",
        await this.loadPart(this.nextPart),
        event !== undefined,
        this.nextPart.page
      );

      this.nextPart = this.parts.get(this.nextPart.page + 1);

      if (!this.nextPart && this.nextButton) {
        this.nextButton.remove();
      }
    } finally {
      this._loadingNext = false;
    }
  };

  getLoadedPageRange = () => {
    let firstLoadedPage = Infinity;
    let lastLoadedPage = -Infinity;
    this.parts.forEach((part) => {
      if (part.isLoaded) {
        firstLoadedPage = Math.min(firstLoadedPage, part.page);
        lastLoadedPage = Math.max(lastLoadedPage, part.page);
      }
    });
    return {
      first: firstLoadedPage === Infinity ? this.data.current_page : firstLoadedPage,
      last: lastLoadedPage === -Infinity ? this.data.current_page : lastLoadedPage,
    };
  };

  updateScrollTargets = () => {
    if (!this.grid || !this.intersectionObserver) return;

    const firstProduct = this.grid.firstElementChild;
    const lastProduct = this.grid.lastElementChild;

    if (!firstProduct || !lastProduct || firstProduct === lastProduct) return;

    this.unobserveIntersection(this._scrollTargetTop);
    this.unobserveIntersection(this._scrollTargetBottom);

    delete this._scrollTargetTop?.dataset.scrollTarget;
    delete this._scrollTargetBottom?.dataset.scrollTarget;

    const { first: firstPage, last: lastPage } = this.getLoadedPageRange();

    firstProduct.dataset.scrollTarget = "top";
    if (!firstProduct.dataset.page) firstProduct.dataset.page = String(firstPage);

    lastProduct.dataset.scrollTarget = "bottom";
    if (!lastProduct.dataset.page) lastProduct.dataset.page = String(lastPage);

    this._scrollTargetTop = firstProduct;
    this._scrollTargetBottom = lastProduct;

    this.observeIntersection(this._scrollTargetTop);
    this.observeIntersection(this._scrollTargetBottom);

    Array.from(this.grid.children).forEach((el) => {
      if (el.dataset.page != null && !this._observedProducts.has(el)) {
        this._observedProducts.add(el);
        this.observeIntersection(el);
      }
    });
  };

  infiniteScrollIntersectionObserver = () => {
    if (!this.grid) return;

    this._observedProducts = new Set();

    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          const target = entry.target;
          const page = target.dataset.page;
          const scrollTarget = target.dataset.scrollTarget;

          if (page) {
            this.updatePageParam(Number(page));
          }

          if (scrollTarget === "bottom" && !this._loadingNext && this.nextPart) {
            this.loadNext();
          } else if (
            scrollTarget === "top" &&
            !this._loadingPrevious &&
            this.previousPart
          ) {
            this.loadPrevious();
          }
        });
      },
      {
        threshold: [0, 1],
        rootMargin: "400px",
      }
    );

    // Array.from(this.grid.children).forEach((el) => {
    //   el.dataset.page = String(this.data.current_page);
    // });

    this.updateScrollTargets();
  };

  observeIntersection = (element) => {
    if (this.intersectionObserver && element) {
      this.intersectionObserver.observe(element);
    }
  };

  unobserveIntersection = (element) => {
    if (this.intersectionObserver && element) {
      this.intersectionObserver.unobserve(element);
    }
  };
}

customElements.define("gtc-pagination", GtcPagination);
